/**
 * Execution & Alerts API Client
 *
 * Provides methods for initiative tracking, anomaly alerts, and execution health.
 */

import axios, { AxiosInstance } from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000'

export interface InitiativeData {
  id: number
  company_id: number
  session_id: string
  initiative_code: string
  title: string
  description: string
  bsc_perspective: string
  pillar_name: string
  owner: string | null
  status: string
  priority: string
  start_date: string | null
  target_date: string | null
  completed_date: string | null
  blockers: any[]
  notes: string | null
  ivp_weight: number | null
  created_at: string
  updated_at: string
}

export interface AlertData {
  id: number
  company_id: number
  alert_type: string
  severity: string
  title: string
  description: string | null
  metric_key: string | null
  current_value: number | null
  threshold_value: number | null
  related_nodes: string[] | null
  status: string
  created_at: string
  resolved_at: string | null
}

export interface ExecutionHealthEntry {
  initiative_id: number
  title: string
  status: string
  health: 'on_track' | 'at_risk' | 'off_track'
  bsc_perspective: string
}

class ExecutionApiClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: { 'Content-Type': 'application/json' },
      timeout: 30000,
    })

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('jwt_token')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    })

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('jwt_token')
          window.location.href = 'http://127.0.0.1:5000'
        }
        return Promise.reject(error)
      }
    )
  }

  // Execution
  async importInitiatives(companyId: number, sessionId: string): Promise<{ status: string; imported: number }> {
    const response = await this.client.post(`/execution/${companyId}/initiatives/import`, { session_id: sessionId })
    return response.data
  }

  async listInitiatives(companyId: number, filters?: { status?: string; perspective?: string }): Promise<{ initiatives: InitiativeData[] }> {
    const params = new URLSearchParams()
    if (filters?.status) params.set('status', filters.status)
    if (filters?.perspective) params.set('perspective', filters.perspective)
    const response = await this.client.get(`/execution/${companyId}/initiatives?${params}`)
    return response.data
  }

  async updateInitiative(companyId: number, id: number, data: Partial<InitiativeData>): Promise<{ status: string }> {
    const response = await this.client.put(`/execution/${companyId}/initiatives/${id}`, data)
    return response.data
  }

  async getDashboard(companyId: number): Promise<any> {
    const response = await this.client.get(`/execution/${companyId}/dashboard`)
    return response.data
  }

  async getHealth(companyId: number): Promise<{ health: ExecutionHealthEntry[]; stalled: any[]; replan_trigger: any; escalated_blockers: any[] }> {
    const response = await this.client.get(`/execution/${companyId}/health`)
    return response.data
  }

  // Alerts
  async listAlerts(companyId: number, status: string = 'active'): Promise<{ alerts: AlertData[] }> {
    const response = await this.client.get(`/alerts/${companyId}?status=${status}`)
    return response.data
  }

  async triggerScan(companyId: number): Promise<any> {
    const response = await this.client.post(`/alerts/${companyId}/scan`)
    return response.data
  }

  async acknowledgeAlert(companyId: number, alertId: number): Promise<{ status: string }> {
    const response = await this.client.put(`/alerts/${companyId}/${alertId}/acknowledge`)
    return response.data
  }

  async resolveAlert(companyId: number, alertId: number): Promise<{ status: string }> {
    const response = await this.client.put(`/alerts/${companyId}/${alertId}/resolve`)
    return response.data
  }
}

export const executionApi = new ExecutionApiClient()
